<template>
  <div>
    <h2 class="primary--text mb-3">
      Delivery Requests
    </h2>

    <v-row>
      <v-col
        cols="12"
        class="py-0"
      >
        <v-tabs
          active-class="secondary--text"
          hide-slider
          background-color="transparent"
        >
          <v-tab
            v-for="tab in tabs"
            :key="tab.title"
            @click="changeCurrentTab(tab)"
          >
            <span class="text-capitalize">{{ tab.title }}</span>
            <span
              v-if="tab.count"
              class="mx-1"
            >({{ tab.count }})</span>
          </v-tab>
        </v-tabs>
      </v-col>

      <v-col cols="12">
        <v-card
          outlined
          class="px-4"
        >
          <v-row justify="start">
            <v-col
              cols="6"
              md="4"
            >
              <v-text-field
                v-model="filters.deliveryRequestId"
                label="Search By ID"
                prepend-inner-icon="mdi-magnify"
                @change="fetchDeliveryRequests"
              />
            </v-col>
            <v-col
              v-if="currentTab === 'ALL'"
              cols="6"
              md="4"
            >
              <v-select
                v-model="filters.status"
                :items="tabs"
                label="Filter by Status"
                item-text="title"
                item-value="value"
                prepend-inner-icon="mdi-truck-outline"
                @change="fetchDeliveryRequests"
              />
            </v-col>
            <v-col
              cols="6"
              md="4"
            >
              <h-date-range-picker
                v-model="filters.dateRange"
                label="Filter by Date"
                @input="fetchDeliveryRequests"
              />
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card>
          <v-data-table
            :items="deliveryRequests"
            :headers="headers"
            :loading="isLoading"
            :server-items-length="1"
            :items-per-page="dataTableFilters.rowsPerPage"
            hide-default-footer
            must-sort
            @update:sort-by="handleSort"
            @update:sort-desc="handleSortDesc"
          >
            <template v-slot:item="props">
              <tr>
                <td class="text-no-wrap">
                  {{ props.item.deliveryRequestId || "N/A" }}
                </td>
                <td>
                  <span class="text-no-wrap">{{
                    humanizeDateFromSeconds(
                      props.item.createdAt,
                      "MMMM Do YYYY, h:mm A"
                    ) || "N/A"
                  }}</span>
                </td>
                <td class="text-center">
                  {{ props.item.numberOfRoutes || "N/A" }}
                </td>
                <td class="text-center">
                  <v-chip
                    v-if="props.item.status"
                    dark
                    label
                    :color="statusMap[props.item.status].color"
                  >
                    {{ statusMap[props.item.status].text }}
                  </v-chip>
                  <span v-else>N/A</span>
                </td>

                <td
                  width="5%"
                  class="text-center"
                >
                  <div class="d-flex align-center justify-center">
                    <v-icon
                      color="grey"
                      @click="
                        $router.push(
                          `delivery-request-overview/${props.item.id}`
                        )
                      "
                      v-text="'mdi-eye-outline'"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>

          <v-divider />

          <div class="d-flex pa-3">
            <v-spacer />
            <v-btn
              :disabled="dataTableFilters.currentPage === 1"
              icon
              class="mx-3"
              @click="previousPage"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              :disabled="
                deliveryRequests.length < dataTableFilters.rowsPerPage
              "
              icon
              class="mx-3"
              @click="nextPage"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="snackbar.isOpen"
      :color="snackbar.color"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { humanizeDateFromSeconds } from '@/utils/misc';
import { parseDeliveryRequest } from '@/utils/documentParser';
import { deliveryRequestsCollection, secureAuthSupplier } from '@/firebase';

import moment from 'moment';
import dataTableMixin from '@/mixins/dataTable';
import HDateRangePicker from '@/components/commons/HDateRangePicker.vue';

export default {
  components: { HDateRangePicker },
  mixins: [dataTableMixin],
  data() {
    return {
      tabs: [
        { title: 'All Delivery Requests', value: 'ALL' },
        { title: 'Completed', count: '', value: 'Completed' },
        { title: 'Accepted', count: '', value: 'Accepted' },
        // { title: "Delayed", count: "", value: "DE" },
        // { title: "Pending ", count: "", value: "PE" },
        // { title: "Canceled", count: "", value: "CE" },
        // { title: "Returned ", count: "", value: "RE" },
        // { title: "Delivered", count: "", value: "DL" },
        // { title: "In Transit", count: "", value: "IT" },
      ],
      headers: [
        {
          text: 'Delivery Requests ID',
          align: 'start',
          value: 'po_id',
          sortable: false,
        },
        { text: 'Date/Time', value: 'created_at', sortable: false },
        {
          text: 'No. of Routes',
          value: 'number_of_routes',
          align: 'center',
          sortable: false,
        },
        {
          text: 'PO Status',
          value: 'status',
          align: 'center',
          sortable: false,
        },
        { text: 'Actions', align: 'center', sortable: false },
      ],
      currentTab: 'ALL',
      filters: {
        deliveryRequestId: '',
        status: 'ALL',
        dateRange: {
          startDate: moment().startOf('month').format('YYYY-MM-DD'),
          endDate: moment().endOf('month').format('YYYY-MM-DD'),
        },
      },
      // statusMap: {
      //   ALL: { text: "", color: "" },
      //   CE: { text: "Cancelled", color: "red" },
      //   DE: { text: "Delayed", color: "amber" },
      //   RE: { text: "Returned", color: "grey" },
      //   AR: { text: "Archived", color: "grey" },
      //   PE: { text: "Pending", color: "orange" },
      //   AC: { text: "Accepted", color: "green" },
      //   DL: { text: "Delivered", color: "green" },
      //   IT: { text: "In Transit", color: "blue" },
      // },
      statusMap: {
        ALL: { text: '', color: '' },
        Completed: { text: 'Completed', color: 'green' },
        Accepted: { text: 'Accepted', color: 'primary' },
      },
      deliveryRequestsDocs: [],
      isLoading: false,
      snackbar: {
        text: '',
        isOpen: false,
        color: 'green',
      },
      unSubscribeDeliveryRequests: () => {},
    };
  },
  computed: {
    deliveryRequests() {
      return this.deliveryRequestsDocs.map(parseDeliveryRequest);
    },
  },
  created() {
    this.inititlizeDataTable({
      apiData: 'deliveryRequestsDocs',
      sortBy: 'created_at',
      apiCallback: 'fetchDeliveryRequests',
    });
  },
  mounted() {
    this.fetchDeliveryRequests();
  },
  beforeDestroy() {
    this.unSubscribeDeliveryRequests();
  },
  methods: {
    humanizeDateFromSeconds,
    fetchDeliveryRequests() {
      this.isLoading = true;

      let query = this.generateFilters(deliveryRequestsCollection);
      query = this.getPaginationQuery(query);

      this.unSubscribeDeliveryRequests();
      this.unSubscribeDeliveryRequests = query.onSnapshot(
        (querySnapshot) => {
          this.deliveryRequestsDocs = querySnapshot.docs;
          this.isLoading = false;
        },
        () => {
          this.displaySnackbar({
            text: 'An error occured. Please try again',
            color: 'red',
          });
          this.isLoading = false;
        },
      );
    },
    generateFilters(originalQuery) {
      let query = originalQuery;

      if (this.currentTab !== 'ALL' || this.filters.status !== 'ALL') {
        const status = [this.currentTab, this.filters.status].find(
          (individualStatus) => individualStatus !== 'ALL',
        );
        query = query.where('status', '==', status);
      }

      if (this.filters.deliveryRequestId) {
        query = query.where(
          'external_id',
          '==',
          this.filters.deliveryRequestId,
        );
      }

      if (this.filters.dateRange.startDate || this.filters.dateRange.endDate) {
        const start = new Date(this.filters.dateRange.startDate);
        const end = new Date(this.filters.dateRange.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        query = query
          .where('created_at', '>=', start)
          .where('created_at', '<=', end);
      }

      return secureAuthSupplier(query);
    },
    changeCurrentTab(tab) {
      this.currentTab = tab.value;
      this.filters.status = 'ALL';
      this.fetchDeliveryRequests();
    },
    displaySnackbar({ text, color }) {
      this.snackbar = {
        text,
        color,
        isOpen: true,
      };
    },
  },
};
</script>
